/**
 * UI Store — panel visibility, workspace mode, status bar
 */
import { createStore } from 'zustand/vanilla';
import type { PanelVisibility, WorkspaceMode } from '@graphicsim/shared-types';
import { eventBus } from '../engine/eventBus';
import { getLayoutVisibility } from '../engine/layoutEngine';

export type WorkspaceFocus = 'none' | 'project' | 'shots';

export type PreviewBackground = 'program-monitor' | 'shot-layout' | 'black' | 'grey' | 'white' | 'alpha' | 'image';

export type LayoutPreset = 'compact' | 'default' | 'express' | 'live' | 'scoretable' | 'setup' | 'custom';

export interface UIState {
  workspace: WorkspaceMode;
  panels: PanelVisibility;
  statusText: string;
  toolbarVisible: boolean;
  previewBackground: PreviewBackground;
  previewBackgroundImage: string;
  activeLayout: LayoutPreset;
  customLayoutPanels: PanelVisibility | null;
  finalRenderQuality: boolean;
  useInstantSearch: boolean;
  allowKeyboardMatching: boolean;
  forceAeOffline: boolean;
  language: string;
  recentProjects: string[];
  workspaceFocus: WorkspaceFocus;
  // Actions
  setWorkspace: (mode: WorkspaceMode) => void;
  togglePanel: (panel: keyof PanelVisibility) => void;
  setPanel: (panel: keyof PanelVisibility, visible: boolean) => void;
  setStatus: (text: string) => void;
  toggleToolbar: () => void;
  setPreviewBackground: (bg: PreviewBackground) => void;
  setPreviewBackgroundImage: (url: string) => void;
  setActiveLayout: (layout: LayoutPreset) => void;
  saveCustomLayout: () => void;
  toggleFinalRenderQuality: () => void;
  toggleInstantSearch: () => void;
  toggleKeyboardMatching: () => void;
  toggleForceAeOffline: () => void;
  setLanguage: (lang: string) => void;
  addRecentProject: (name: string) => void;
  clearRecentProjects: () => void;
  resetPanels: () => void;
  setWorkspaceFocus: (focus: WorkspaceFocus) => void;
}

export const uiStore = createStore<UIState>((set, get) => ({
  workspace: 'setup',
  panels: { ...getLayoutVisibility('setup') },
  statusText: '',
  toolbarVisible: true,
  previewBackground: 'alpha',
  previewBackgroundImage: '',
  activeLayout: 'setup',
  customLayoutPanels: null,
  finalRenderQuality: false,
  useInstantSearch: false,
  allowKeyboardMatching: false,
  forceAeOffline: false,
  language: 'English',
  recentProjects: [],
  workspaceFocus: 'none',

  setWorkspace: (mode) => {
    set({ workspace: mode });
    eventBus.emit('ui:workspaceChanged', { mode });
    // Clicking Setup/Live toggle applies the corresponding layout
    if (mode === 'setup') {
      get().setActiveLayout('setup');
    } else if (mode === 'live') {
      get().setActiveLayout('live');
    }
  },

  togglePanel: (panel) => {
    const current = get().panels[panel];
    set({ panels: { ...get().panels, [panel]: !current } });
    eventBus.emit('ui:panelToggled', { panel, visible: !current });
  },

  setPanel: (panel, visible) => {
    if (get().panels[panel] !== visible) {
      set({ panels: { ...get().panels, [panel]: visible } });
      eventBus.emit('ui:panelToggled', { panel, visible });
    }
  },

  setStatus: (text) => set({ statusText: text }),

  toggleToolbar: () => set({ toolbarVisible: !get().toolbarVisible }),

  setPreviewBackground: (bg) => set({ previewBackground: bg }),

  setPreviewBackgroundImage: (url) => set({ previewBackgroundImage: url, previewBackground: 'image' }),

  setActiveLayout: (layout) => {
    set({ activeLayout: layout });
    eventBus.emit('ui:layoutChanged', { layout });
    let panels: PanelVisibility | null = null;
    if (layout === 'custom') {
      panels = get().customLayoutPanels;
    } else {
      panels = getLayoutVisibility(layout);
    }
    if (panels) {
      set({ panels: { ...panels }, toolbarVisible: true });
      for (const [panel, visible] of Object.entries(panels)) {
        eventBus.emit('ui:panelToggled', { panel: panel as keyof PanelVisibility, visible });
      }
    }
  },

  saveCustomLayout: () => {
    set({ customLayoutPanels: { ...get().panels }, activeLayout: 'custom' });
  },

  toggleFinalRenderQuality: () => set({ finalRenderQuality: !get().finalRenderQuality }),

  toggleInstantSearch: () => set({ useInstantSearch: !get().useInstantSearch }),

  toggleKeyboardMatching: () => set({ allowKeyboardMatching: !get().allowKeyboardMatching }),

  toggleForceAeOffline: () => set({ forceAeOffline: !get().forceAeOffline }),

  setLanguage: (lang) => set({ language: lang }),

  addRecentProject: (name) => {
    const list = get().recentProjects.filter((n) => n !== name);
    list.unshift(name);
    set({ recentProjects: list.slice(0, 5) });
  },

  clearRecentProjects: () => set({ recentProjects: [] }),

  resetPanels: () => {
    const setupPanels = getLayoutVisibility('setup');
    set({ panels: { ...setupPanels }, toolbarVisible: true, activeLayout: 'setup' });
    // Emit events so toolbar/panels re-render
    for (const [panel, visible] of Object.entries(setupPanels)) {
      eventBus.emit('ui:panelToggled', { panel: panel as keyof PanelVisibility, visible });
    }
  },

  setWorkspaceFocus: (focus) => {
    // Toggle off if same focus clicked again
    const current = get().workspaceFocus;
    const newFocus = current === focus ? 'none' : focus;
    set({ workspaceFocus: newFocus });
    // Ensure focused panel is visible
    if (newFocus === 'shots') {
      get().setPanel('shots', true);
    }
  },
}));
